import axios, { AxiosInstance } from 'axios';
import { wrapper } from 'axios-cookiejar-support';
import { CookieJar } from 'tough-cookie';
import { authUserSignal } from '../signals/authSignal';
import { createLogger } from '../utils/logger';

const logger = createLogger('AuthHTTPService');

// Vite exposes env vars via import.meta.env (must be prefixed with VITE_)
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4500/api';

// require:
// npm install axios tough-cookie axios-cookiejar-support
export class AuthHTTPService {
  private authHttpClient: AxiosInstance;
  // Set lazily via setAuthService() to avoid a circular import/instantiation
  // dependency between AuthHTTPService <-> AuthService.
  private authService: { refreshLogin: () => Promise<boolean> } | undefined;

  constructor() {
    logger.debug('baseURL=', BASE_URL);

    this.authHttpClient = wrapper(
      axios.create({
        jar: new CookieJar(),
        withCredentials: true,
        baseURL: BASE_URL,
      })
    );

    this.authHttpClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        logger.debug(
          'interceptor in response status=',
          error.response?.status,
          'url=',
          error.config?.url
        );
        const prevRequest = error?.config;

        if (
          error?.response?.status === 403 &&
          !prevRequest.sent &&
          prevRequest.url.indexOf('refresh_token') > 0
        ) {
          prevRequest.sent = true;
          logger.debug('httpClient callback for refresh_token expired on backend');
          authUserSignal.value = undefined;
          window.location.reload();
        } else if (error?.response?.status === 403 && !prevRequest.sent) {
          logger.debug('trying refreshLogin(), url=', prevRequest.url);
          prevRequest.sent = true; // prevents infinite loop for 403 error codes
          if (this.authService) {
            await this.authService.refreshLogin();
          }
          return this.authHttpClient(prevRequest);
        } else if (error?.response?.status === 401) {
          // See HttpService.ts for why clearing the signal alone isn't enough to redirect.
          logger.debug('httpClient callback for 401, redirecting to /login');
          authUserSignal.value = undefined;
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Wire up the AuthService instance after both have been constructed.
   * This breaks the circular dependency between AuthHTTPService and AuthService.
   */
  public setAuthService(authService: { refreshLogin: () => Promise<boolean> }) {
    this.authService = authService;
  }

  public getClient() {
    return this.authHttpClient;
  }
}
